/**
 * Delegation Authority Lab — guided bounded-agency brief builder.
 *
 * Reuses content from boundedAgencyDelegation.ts and stage/locus types from
 * fieldOfPlay.ts. Pure functions here power the wizard and "show state,
 * suggest the move" diagnostics.
 */

import {
  DELEGATION_LEVELS,
  lookupMatrixCell,
  type Delegate,
  type DelegationBrief,
  type Reversibility,
  type RiskBand,
} from '../data/boundedAgencyDelegation';
import type { LevelId, StageId } from '../data/fieldOfPlay';

export const STORAGE_KEY = 'lab_delegation_authority_v01';

export type DelegationLabStep =
  | 'intro'
  | 'outcome'
  | 'risk'
  | 'level'
  | 'envelope'
  | 'delegate'
  | 'brief'
  | 'complete';

export const DELEGATION_LAB_STEPS: { id: DelegationLabStep; label: string; short: string }[] = [
  { id: 'intro', label: 'Bounded agency', short: 'Start' },
  { id: 'outcome', label: 'Outcome & placement', short: 'Outcome' },
  { id: 'risk', label: 'Risk envelope', short: 'Risk' },
  { id: 'level', label: 'Authority ladder', short: 'Level' },
  { id: 'envelope', label: 'Operating envelope', short: 'Envelope' },
  { id: 'delegate', label: 'Human or AI', short: 'Delegate' },
  { id: 'brief', label: 'Delegation brief', short: 'Brief' },
  { id: 'complete', label: 'Done', short: 'Done' },
];

export interface DelegationLabState {
  outcome: string;
  stage: StageId;
  locus: LevelId;
  reversibility: Reversibility;
  risk: RiskBand;
  level: number;
  delegate: Delegate;
  authority: string;
  boundaries: string;
  escalation: string;
  reporting: string;
  definitionOfDone: string;
  aiGuardrails: Record<string, boolean>;
  completedAt?: string;
}

export type DelegationLabSeverity = 'info' | 'warn' | 'risk';

export interface DelegationLabTension {
  id: string;
  severity: DelegationLabSeverity;
  message: string;
  suggestedPlay?: string;
}

export interface DelegationLabReading {
  observations: string[];
  tensions: DelegationLabTension[];
  suggestedPlay?: string;
}

const RELEASE_WORDS = /\b(send|publish|post|commit|deploy|release|share externally|hire|fire|sign|approve)\b/i;

export function createDefaultDelegationLabState(): DelegationLabState {
  const cell = lookupMatrixCell('reversible', 'moderate');
  return {
    outcome: '',
    stage: 'execute',
    locus: 'team',
    reversibility: 'reversible',
    risk: 'moderate',
    level: cell.maxLevel,
    delegate: 'human',
    authority: '',
    boundaries: '',
    escalation: '',
    reporting: '',
    definitionOfDone: '',
    aiGuardrails: {},
  };
}

export function stepIndex(step: DelegationLabStep): number {
  return DELEGATION_LAB_STEPS.findIndex((s) => s.id === step);
}

export function nextStep(step: DelegationLabStep): DelegationLabStep | null {
  const idx = stepIndex(step);
  if (idx < 0 || idx >= DELEGATION_LAB_STEPS.length - 1) return null;
  return DELEGATION_LAB_STEPS[idx + 1].id;
}

export function prevStep(step: DelegationLabStep): DelegationLabStep | null {
  const idx = stepIndex(step);
  if (idx <= 0) return null;
  return DELEGATION_LAB_STEPS[idx - 1].id;
}

export function suggestMaxLevel(reversibility: Reversibility, risk: RiskBand): number {
  return lookupMatrixCell(reversibility, risk).maxLevel;
}

export function levelDef(level: number) {
  return DELEGATION_LEVELS.find((l) => l.level === level);
}

export interface LevelPrefill {
  authority: string;
  reporting: string;
  escalation: string;
}

/** Seed envelope hints from the selected delegation level. */
export function prefillFromLevel(level: number): LevelPrefill {
  const def = levelDef(level);
  if (!def) {
    return { authority: '', reporting: '', escalation: '' };
  }
  return {
    authority: def.agency,
    reporting: def.feedback,
    escalation: def.escalation,
  };
}

export function toDelegationBrief(state: DelegationLabState): DelegationBrief {
  return {
    level: state.level,
    delegate: state.delegate,
    outcome: state.outcome,
    authority: state.authority,
    boundaries: state.boundaries,
    escalation: state.escalation,
    reporting: state.reporting,
    definitionOfDone: state.definitionOfDone,
  };
}

export function readDelegationLab(state: DelegationLabState): DelegationLabReading {
  const observations: string[] = [];
  const tensions: DelegationLabTension[] = [];
  const maxLevel = suggestMaxLevel(state.reversibility, state.risk);
  const def = levelDef(state.level);
  const cell = lookupMatrixCell(state.reversibility, state.risk);

  if (state.outcome.trim()) {
    observations.push(`Desired outcome: "${state.outcome.trim()}".`);
  }
  observations.push(
    `Risk envelope: ${state.reversibility}, ${state.risk} risk — matrix recommends up to Level ${maxLevel}.`,
  );
  if (def) {
    observations.push(`Selected Level ${state.level} — ${def.name} (${def.role}).`);
  }
  observations.push(`Delegate: ${state.delegate === 'ai' ? 'AI' : 'human'}.`);

  if (state.level > maxLevel) {
    tensions.push({
      id: 'level-exceeds-matrix',
      severity: 'risk',
      message: `Level ${state.level} exceeds the recommended maximum (Level ${maxLevel}) for ${state.reversibility}, ${state.risk}-risk work.`,
      suggestedPlay: cell.guidance,
    });
  }

  if (
    state.delegate === 'ai' &&
    state.level >= 7 &&
    (state.reversibility === 'irreversible' || state.risk === 'high')
  ) {
    tensions.push({
      id: 'ai-full-autonomy-high-stakes',
      severity: 'risk',
      message:
        'Full outcome ownership (Level 7) for AI on irreversible or high-risk work is usually unsafe. Prefer Level 5–6 with an explicit operating envelope.',
      suggestedPlay: 'Define escalation triggers and audit trail before assigning.',
    });
  }

  if (state.level === 4) {
    const combined = `${state.outcome} ${state.authority}`;
    if (RELEASE_WORDS.test(combined)) {
      tensions.push({
        id: 'draft-with-release-language',
        severity: 'warn',
        message:
          'Level 4 is draft-for-review — language suggests send/publish/commit. Confirm the delegate must not release without approval.',
        suggestedPlay: 'Clarify that preparation does not equal permission to publish.',
      });
    }
  }

  if (state.level >= 5 && !state.escalation.trim()) {
    tensions.push({
      id: 'missing-escalation',
      severity: 'warn',
      message: `Level ${state.level} grants meaningful autonomy — escalation triggers should be explicit.`,
      suggestedPlay: def?.escalation ?? 'Define when to stop and come back.',
    });
  }

  if (state.level >= 3 && !state.boundaries.trim()) {
    tensions.push({
      id: 'missing-boundaries',
      severity: 'info',
      message: 'No boundaries specified. Trust without boundaries becomes ambiguity.',
      suggestedPlay: 'Name what the delegate may not do without approval.',
    });
  }

  const suggestedPlay =
    tensions.find((t) => t.severity === 'risk')?.suggestedPlay ??
    tensions.find((t) => t.severity === 'warn')?.suggestedPlay ??
    (state.level <= maxLevel
      ? 'Delegation envelope looks aligned with the risk matrix.'
      : undefined);

  return { observations, tensions, suggestedPlay };
}

export function canAdvance(step: DelegationLabStep, state: DelegationLabState): boolean {
  switch (step) {
    case 'intro':
    case 'risk':
    case 'level':
    case 'brief':
    case 'complete':
      return true;
    case 'outcome':
      return state.outcome.trim().length >= 8;
    case 'envelope':
      return (
        state.authority.trim().length >= 4 &&
        state.definitionOfDone.trim().length >= 4
      );
    case 'delegate':
      return true;
    default:
      return false;
  }
}
